import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../services/auth.service';
import { ApplicationContextService } from '../services/application-context.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {


    constructor(
        private authenticationService: AuthenticationService,
        private applicationContextService: ApplicationContextService,
    ) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Stedi Healthcare APIs authenticate with a raw API key in Authorization —
        // do not overwrite with the clinic Bearer JWT.
        if (this.isStediRequest(request)) {
            return next.handle(request);
        }

        if (this.authenticationService.isIdentityRefreshRequest(request.url)) {
            return next.handle(request);
        }

        // Aya private API sets its own lane headers (X-Aya-Org-Id, etc.).
        // Extra X-Organization-Id breaks CORS preflight on the dev-aya BFF.
        if (this.isAyaPrivateRequest(request)) {
            return next.handle(request);
        }

        return from(this.authenticationService.getValidAccessToken()).pipe(
            switchMap((token) => {
                const trimmed = token?.trim() || '';
                const aiRequest = this.isAiApiRequest(request);

                if (aiRequest && !trimmed) {
                    return throwError(
                        () =>
                            new Error(
                                '[clinic-auth] Identity token required for AI API. Sign in again via the auth app.',
                            ),
                    );
                }

                const headers: Record<string, string> = {};
                if (trimmed) {
                    headers.Authorization = `Bearer ${trimmed}`;
                    const orgId =
                        this.applicationContextService.contextSnapshot
                            ?.currentlyLoggedInUser?.companyId?.trim() ||
                        this.authenticationService.resolveActiveOrganizationId() ||
                        null;
                    if (
                        orgId &&
                        this.authenticationService.isIdentityAccessToken(trimmed)
                    ) {
                        headers['X-Organization-Id'] = orgId;
                    }
                }
                if (Object.keys(headers).length) {
                    request = request.clone({ setHeaders: headers });
                }
                return next.handle(request);
            })
        );
    }

    /** as-ai-v2 (Firebase / Entra Bearer + optional X-Organization-Id). */
    private isAiApiRequest(request: HttpRequest<unknown>): boolean {
        const url = (request.url ?? '').toLowerCase();
        const aiBase = (environment.aiApiUrl || '')
            .trim()
            .replace(/\/+$/, '')
            .toLowerCase();
        if (aiBase && url.startsWith(aiBase)) {
            return true;
        }
        return url.includes('as-ai-v2');
    }

    private isStediRequest(request: HttpRequest<unknown>): boolean {
        const url = request.url ?? '';
        return (
            url.includes('payers.us.stedi.com') ||
            url.includes('healthcare.us.stedi.com') ||
            url.startsWith('/stedi-payers') ||
            url.includes('/stedi-payers/') ||
            url.startsWith('/stedi-healthcare') ||
            url.includes('/stedi-healthcare/')
        );
    }

    private isAyaPrivateRequest(request: HttpRequest<unknown>): boolean {
        const url = request.url ?? '';
        const base = (environment.ayaApiBaseUrl || '').replace(/\/+$/, '');
        if (base && url.startsWith(base)) {
            return true;
        }
        return (
            url.includes('/api/aya/') ||
            url.includes('healthexam.ai/api/aya')
        );
    }
}
